import { Table, type TableColumn } from "@repo/ui/components/data-table";
import { Button } from "@repo/ui/ui/button";
import { Spinner } from "@repo/ui/ui/spinner";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { downloadCSV } from "~/libs/download-csv";
import { useRiftQuery } from "~/stores/dataset-rifts";
import { RunPayloadError } from "~/stores/datasets/rift-dataset-payload.query";
import { reexecuteRun } from "~/stores/run-results/run-reexecute";
import { useRunRift } from "~/stores/run-results/run-rift";
import { RunResultActions } from "./run-result-actions";

/**
 * The `runQuery` tool output persisted on a chat message. `runId` is absent on
 * messages persisted before the run-results foundation shipped — those render
 * in sample-only mode (no hydration, no CSV).
 */
export type RunQueryToolOutput = {
  runId?: string;
  query: string;
  /** Pre-LIMIT-injection SQL; absent on legacy messages (fall back to query). */
  originalQuery?: string;
  columns: { name: string; type: string }[];
  totalRows: number;
  sampleRows: Record<string, unknown>[];
  truncated: boolean;
  runtimeMs: number;
  error?: string;
};

const ROW_PX = 28;
const HEADER_PX = 32;
/** Rows shown before the table body scrolls internally. */
const VISIBLE_ROWS = 10;
/** The server caps chat/MCP executions at this many rows (see `ensureLimit`). */
const ROW_CAP = 100;

/**
 * Renders a chat `runQuery` result as a real data table. Sample rows paint
 * instantly from the persisted tool output while the full immutable run
 * hydrates through the shared rift query layer (`useRunRift` → `useRiftQuery`).
 * No `blocks$`/board coupling — a run-only rift descriptor drives the table.
 */
export const RunResultTable = ({
  output,
  title,
}: {
  output: RunQueryToolOutput;
  /** The runQuery tool's `title` input, used to name a saved dataset/block. */
  title?: string;
}) => {
  const [rerunning, setRerunning] = useState(false);
  // Some persisted tool outputs (failed runs, pre-field-existence messages)
  // arrive without these arrays, so normalise before touching them.
  const outputColumns = output.columns ?? [];
  const outputSampleRows = output.sampleRows ?? [];
  // Ephemeral column layout — resets on unmount / thread reload. Persistent
  // config is a future chat-block entity's job, not this component's.
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    outputColumns.map((col) => col.name),
  );
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  const runId = output.runId ?? null;
  const { riftId } = useRunRift(runId);
  const query = useRiftQuery(riftId);

  const riftData = query.data?.data ?? null;
  const riftColumns = query.data?.columns ?? null;
  const hydrated = !!riftData;

  const payloadError =
    query.error instanceof RunPayloadError ? query.error : null;
  const expired = payloadError?.reason === "expired";
  // A non-expired load failure (network/timeout/duckdb) we can simply retry.
  const loadFailed = query.isError && !expired;

  const columns = useMemo<TableColumn<Record<string, unknown>>[]>(
    () =>
      outputColumns.map((col) => ({
        id: col.name,
        accessorFn: (row: Record<string, unknown>) => row[col.name],
        meta: { displayName: col.name },
      })),
    [outputColumns],
  );

  // Full rows once hydrated; otherwise the persisted sample. Chat runs are
  // capped at 100 rows server-side, so materialising every row is cheap.
  const rows = useMemo<Record<string, unknown>[]>(
    () => (riftData ? riftData.toRows() : outputSampleRows),
    [riftData, outputSampleRows],
  );

  // Server-side query failure — there is no result to render.
  if (output.error) {
    return (
      <div className="mt-1 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-destructive text-xs">
        {output.error}
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="mt-1 rounded-md border p-3 text-muted-foreground text-xs">
        Query returned no columns.
      </div>
    );
  }

  const rowCount = hydrated ? (riftData?.numRows ?? 0) : output.totalRows;
  const tableHeight =
    Math.min(Math.max(rows.length, 1), VISIBLE_ROWS) * ROW_PX + HEADER_PX;

  const handleCsv = () => {
    if (!riftData || !riftColumns) return;
    downloadCSV("query-result", riftColumns, riftData);
  };

  const handleRerun = async () => {
    if (!runId) return;
    setRerunning(true);
    try {
      await reexecuteRun(runId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to re-run the query.",
      );
    } finally {
      setRerunning(false);
    }
  };

  return (
    <div className="mt-1 overflow-hidden rounded-md border">
      <div className="flex items-center justify-between gap-2 border-b px-2 py-1.5">
        <span className="truncate font-medium text-foreground text-xs">
          {title?.trim() || "Query result"}
        </span>
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <div className="flex items-center gap-1.5">
            {!hydrated && runId && !expired && !loadFailed && (
              <Spinner className="size-3.5" />
            )}
            <span>
              {hydrated
                ? `${rowCount} row${rowCount === 1 ? "" : "s"}${
                    rowCount >= ROW_CAP ? " (row limit)" : ""
                  }`
                : `Showing ${outputSampleRows.length} of ${output.totalRows} row${
                    output.totalRows === 1 ? "" : "s"
                  }`}
            </span>
          </div>
          <RunResultActions
            runId={runId}
            name={title ?? ""}
            query={output.originalQuery}
            canDownload={hydrated}
            onDownload={handleCsv}
          />
        </div>
      </div>

      {expired && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/40 p-2 text-muted-foreground text-xs">
          <span>
            This result has expired. Re-run the query to load the full table.
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={rerunning}
            onClick={handleRerun}
          >
            {rerunning ? <Spinner className="size-3.5" /> : null}
            Re-run
          </Button>
        </div>
      )}

      {loadFailed && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/40 p-2 text-muted-foreground text-xs">
          <span>Couldn't load the full result.</span>
          <Button size="sm" variant="outline" onClick={() => query.refetch()}>
            Retry
          </Button>
        </div>
      )}

      <div className="overlay-scrollbar w-full overflow-x-auto">
        <div className="flex flex-col" style={{ height: tableHeight }}>
          <Table<Record<string, unknown>>
            data={rows}
            columns={columns}
            className="text-xs"
            columnOrder={columnOrder}
            onColumnOrderChange={setColumnOrder}
            columnWidths={columnWidths}
            onColumnWidthsChange={setColumnWidths}
          />
        </div>
      </div>
    </div>
  );
};
